/* =====================================================================
 * projectWikiBinding — 문서에이전트에서 만든 프로젝트 위키를 대화에 못박는다
 *
 * 통합에이전트에 위키를 "첨부"하지 않는다. 위키 페이지를 마크다운 파일로 올리면
 * 사람이 미리 고른 페이지만 모델이 보게 되는데, 위키의 값은 **에이전트가 직접
 * 검색해서** 필요한 페이지·원문·그림까지 따라가는 것이다. 그래서 고르는 행위는
 * 첨부가 아니라 **바인딩**이고, 대화에 붙은 위키 하나가 `WikiAgent` 의 조회 범위가
 * 된다 (GTC 가 비교한 두 문서를 대화에 고정하는 것과 같은 구조다).
 *
 * `wiki_id` 는 요청 metadata 로 나가 AI 서버가 세션 state 에 심고, 위키 도구는
 * **거기서만** 읽는다 — 모델 인자로는 받지 않는다. 모델이 위키를 고를 수 있게
 * 두면 프롬프트 인젝션 한 번이 곧 다른 프로젝트 조회 시도가 된다. metadata 자체는
 * 클라이언트가 보내므로 위조할 수 있지만, backend 내부 API 가 사용자 토큰으로
 * 프로젝트 접근 권한을 다시 확인해 막는다.
 *
 * 저장 위치는 워크스페이스 아이템의 `settings.projectWiki` 다. 진실의 원천을 한
 * 곳으로 두려고 localStorage 는 쓰지 않는다 — 두 곳에 두면 한쪽에서 위키를
 * 떼었는데 다른 쪽에 남아 "떼었는데 계속 위키를 본다" 가 된다.
 *
 * 예외가 하나 있다. **아직 대화가 만들어지지 않은 새 채팅**은 저장할 아이템이
 * 없다. 사용자는 첫 메시지를 쓰기 전에 위키를 붙일 수 있으므로, 그 사이만
 * 메모리에 들고 있다가 대화가 생기면 옮긴다 (:func:`adoptDraftProjectWiki`).
 * ===================================================================== */

import { updateItem } from '@auw/api-client';
import { useChatStore } from '@auw/stores';
import { buildConversationSettings, type Conversation } from '@auw/workspace-utils';
import { create } from 'zustand';

export interface ProjectWikiBinding {
  /** backend 위키 식별자. 도구가 이걸로만 조회한다. */
  wikiId: string;
  /** 표시용 프로젝트 이름. 없어도 조회는 된다. */
  title: string | null;
}

/** 아이템 settings 의 키. AI 쪽 metadata 네임스페이스(`project_wiki`)와 짝이다. */
const SETTINGS_KEY = 'projectWiki';

function str(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
}

/** 대화에 저장된 바인딩을 읽는다. 모양이 어긋나면 `null`. */
export function readProjectWikiBinding(
  conv: Conversation | null | undefined,
): ProjectWikiBinding | null {
  const raw = conv?.workspaceSettings?.[SETTINGS_KEY];
  if (!raw || typeof raw !== 'object') return null;
  const record = raw as Record<string, unknown>;
  const wikiId = str(record.wikiId) ?? str(record.wiki_id);
  if (!wikiId) return null;
  return { wikiId, title: str(record.title) };
}

/* ------------------------------------------------------------------ *
 * 새 채팅용 임시 자리
 * ------------------------------------------------------------------ */

interface DraftState {
  draft: ProjectWikiBinding | null;
  setDraft: (binding: ProjectWikiBinding | null) => void;
}

/** 대화가 없는 동안만 쓰는 자리. 저장하지 않으므로 새로고침하면 사라진다. */
export const useProjectWikiDraft = create<DraftState>((set) => ({
  draft: null,
  setDraft: (draft) => set({ draft }),
}));

/**
 * 지금 이 대화에 붙은 위키.
 *
 * 대화가 있으면 저장된 값이, 없으면 방금 고른 임시 값이 답이다. 대화가 있는데
 * 저장된 값이 없으면 **붙지 않은 것이다** — 임시 값으로 대신하면 위키를 뗀 옛
 * 대화에 다른 대화의 선택이 되살아난다.
 */
export function resolveProjectWikiBinding(
  conv: Conversation | null | undefined,
  draft: ProjectWikiBinding | null,
): ProjectWikiBinding | null {
  if (conv) return readProjectWikiBinding(conv);
  return draft;
}

/**
 * 요청에 실을 `metadata.project_wiki`.
 *
 * 키 이름은 AI 쪽 계약이다 (`ai/app/services/agents/shared/wiki_binding.py`).
 */
export function projectWikiRequestMetadata(
  binding: ProjectWikiBinding | null,
): { wiki_id: string; title: string } | null {
  if (!binding?.wikiId) return null;
  return { wiki_id: binding.wikiId, title: binding.title ?? '' };
}

/* ------------------------------------------------------------------ *
 * 저장
 * ------------------------------------------------------------------ */

/**
 * 진행 중인 저장. 다음 저장은 이것이 끝난 뒤에 시작한다.
 *
 * ⚠️ 저장은 아이템 settings **전체를 덮어쓰는** PUT 이다. 두 저장이 겹치면 늦게
 * 출발한 요청이 먼저 도착할 수 있고, 그러면 옛 상태가 새 상태를 지운다 — 위키를
 * 바꿨는데 앞의 것으로 되돌아간다. 줄을 세워 마지막 요청이 마지막 상태를 싣게
 * 한다 (gtcBinding 과 같은 이유).
 */
let saveChain: Promise<void> = Promise.resolve();

async function persist(
  itemId: string,
  binding: ProjectWikiBinding | null,
): Promise<void> {
  try {
    const conv = useChatStore.getState().conversations[itemId];
    if (!conv) return;

    const settings = { ...(conv.workspaceSettings ?? {}) };
    if (binding) {
      settings[SETTINGS_KEY] = binding;
    } else {
      delete settings[SETTINGS_KEY];
    }
    const next: Conversation = { ...conv, workspaceSettings: settings };

    // 낙관적 반영 — 서버 왕복을 기다리면 칩이 한 박자 늦게 뜬다.
    useChatStore.setState((s) => ({
      conversations: { ...s.conversations, [itemId]: next },
    }));

    await updateItem(itemId, { settings: buildConversationSettings(next) });
  } catch (err) {
    // 영속화는 "다음에 다시 열 수 있게" 하는 부가 기능이다. 실패해도 이번 턴의
    // 요청 metadata 는 이미 스토어 상태에서 나가므로 대화 자체는 성립한다.
    console.error('[projectWikiBinding] 위키 바인딩 저장 실패:', err);
  }
}

/**
 * 위키를 대화에 붙이거나 뗀다 (`null` 이면 뗀다).
 *
 * 대화가 아직 없으면 임시 자리에 둔다 — 첫 메시지를 보내면
 * :func:`adoptDraftProjectWiki` 가 실제 대화로 옮긴다.
 */
export function bindProjectWiki(
  conversationId: string | null,
  binding: ProjectWikiBinding | null,
): void {
  if (!conversationId) {
    useProjectWikiDraft.getState().setDraft(binding);
    return;
  }
  // 대화가 정해진 뒤에는 임시 값을 남겨 두지 않는다. 남기면 다음 새 채팅이
  // 이 선택을 물려받는다.
  useProjectWikiDraft.getState().setDraft(null);
  saveChain = saveChain.catch(() => undefined).then(() => persist(conversationId, binding));
}

/**
 * 새 채팅에서 고른 위키를 방금 만들어진 대화로 옮긴다.
 *
 * 이미 저장된 값이 있으면 건드리지 않는다 — 옛 대화를 열었을 때 임시 값이 그
 * 대화의 바인딩을 덮어쓰면 안 된다.
 */
export function adoptDraftProjectWiki(conversationId: string): void {
  const { draft, setDraft } = useProjectWikiDraft.getState();
  if (!draft) return;
  setDraft(null);
  const conv = useChatStore.getState().conversations[conversationId];
  if (readProjectWikiBinding(conv)) return;
  saveChain = saveChain.catch(() => undefined).then(() => persist(conversationId, draft));
}
